// frontend/src/components/blog/ShareButtons.js
//
// Share controls for blog articles. A single "שיתוף" pill adapts to the device:
//   - Mobile: opens the native OS share sheet (WhatsApp / Telegram / Messenger / …).
//   - Desktop: opens WhatsApp share directly (the dominant channel for this audience).
// The full variant (bottom of the article) also shows a copy-link pill; the inline
// variant (on the tags row) is just the single share pill.
//
// Behaviour is decided at click time, so the rendered markup is identical on the
// server/prerender and the client — no hydration mismatch.

import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { Share2, Link2, Check } from "lucide-react";

const BODY_FONT = '"DM Sans", sans-serif';
// Pill LABEL colour (teal text on white at 13.5px), so it is small text and needs
// WCAG AA 4.5:1. The old #00A896 measured 2.98:1; this is 5.47:1.
const ACCENT = "#0F766E";

const pillSx = (fg, bg, border) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 0.6,
  px: 1.4,
  py: 0.7,
  border: `1px solid ${border}`,
  borderRadius: "999px",
  background: bg,
  color: fg,
  fontFamily: BODY_FONT,
  fontWeight: 600,
  fontSize: "13.5px",
  lineHeight: 1,
  cursor: "pointer",
  textDecoration: "none",
  transition: "background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
  "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.10)", transform: "translateY(-1px)" },
  "@media (prefers-reduced-motion: reduce)": { transition: "none", "&:hover": { transform: "none" } },
});

// Treat as "mobile" (use the native share sheet) when the UA looks mobile or the
// primary pointer is coarse + touch-capable. Desktop falls through to WhatsApp.
const isMobileShare = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return true;
  return (
    navigator.maxTouchPoints > 1 &&
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches
  );
};

const ShareButtons = ({ url, title, text, label = "אהבתם? שתפו:", variant = "full" }) => {
  const [copied, setCopied] = useState(false);

  const shareText = text || title;
  const waHref = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${url}`)}`;

  const handleShare = async () => {
    if (
      typeof navigator !== "undefined" &&
      typeof navigator.share === "function" &&
      isMobileShare()
    ) {
      try {
        await navigator.share({ title, text: shareText, url });
      } catch {
        // User dismissed the share sheet — nothing to do.
      }
    } else {
      window.open(waHref, "_blank", "noopener,noreferrer");
    }
  };

  const handleCopy = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked — silently ignore.
    }
  };

  const sharePill = (
    <Box
      component="button"
      type="button"
      onClick={handleShare}
      aria-label="שיתוף"
      sx={{
        ...pillSx(ACCENT, "#FFFFFF", "#CBD5E1"),
        // Inline (tags row): push the pill to the far left. marginInlineStart:auto
        // absorbs the free space on the start (right, in RTL) side, so the tags
        // stay on the right and the pill sits at the left edge on desktop + mobile.
        ...(variant === "inline" ? { marginInlineStart: "auto" } : {}),
      }}
    >
      <Share2 size={16} />
      שיתוף
    </Box>
  );

  if (variant === "inline") return sharePill;

  return (
    <Box
      dir="rtl"
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1,
        my: { xs: 2, md: 2.5 },
      }}
    >
      <Typography
        component="span"
        sx={{ fontFamily: BODY_FONT, fontWeight: 700, fontSize: "13.5px", color: "#64748B", ml: 0.5 }}
      >
        {label}
      </Typography>

      {sharePill}

      <Box
        component="button"
        type="button"
        onClick={handleCopy}
        aria-label="העתקת קישור"
        sx={pillSx(copied ? "#0F766E" : "#475569", copied ? "#ECFDF5" : "#FFFFFF", copied ? "#5EEAD4" : "#CBD5E1")}
      >
        {copied ? <Check size={16} /> : <Link2 size={16} />}
        {copied ? "הועתק!" : "העתק קישור"}
      </Box>
    </Box>
  );
};

export default ShareButtons;
